import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import {
  Avatar, Box, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputAdornment, InputLabel, MenuItem as MuiMenuItem,
  Select, SelectChangeEvent, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Alert, Button,
} from '@mui/material';
import {
  Close, Delete as DeleteIcon, Edit as EditIcon, Search, Fastfood as FastfoodIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';
import LoadingAnimation from '../components/LoadingAnimation';

interface MenuItemRow {
  _id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  images?: string[];
  status: 'active' | 'sold_out' | 'unavailable';
  business?: { _id: string; name: string } | string;
  inventory?: { totalQuantity: number; remainingQuantity: number };
}

const categories = ['Starters', 'Mains', 'Sides', 'Desserts', 'Drinks', 'Breakfast', 'Other'];
const statuses: MenuItemRow['status'][] = ['active', 'sold_out', 'unavailable'];

export default function MenuItems() {
  const [items, setItems] = useState<MenuItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [editItem, setEditItem] = useState<MenuItemRow | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editPrice, setEditPrice] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editStatus, setEditStatus] = useState<MenuItemRow['status']>('active');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<MenuItemRow | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const fetchItems = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get('/api/admin/menu-items', { params: { page, limit: rowsPerPage } });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((item) => {
    const businessName = typeof item.business === 'object' ? item.business?.name : '';
    const q = query.toLowerCase();
    return !q || item.name.toLowerCase().includes(q) || (businessName || '').toLowerCase().includes(q);
  });

  const openEdit = (item: MenuItemRow) => {
    setEditItem(item);
    setEditPrice(String(item.price ?? ''));
    setEditCategory(item.category);
    setEditStatus(item.status);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await axios.put(`/api/admin/menu-items/${editItem._id}`, {
        price: Number(editPrice),
        category: editCategory,
        status: editStatus,
      });
      setEditOpen(false);
      setSnackbar({ open: true, message: 'Menu item updated', severity: 'success' });
      fetchItems();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update menu item', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/api/admin/menu-items/${deleteTarget._id}`);
      setDeleteTarget(null);
      setSnackbar({ open: true, message: 'Menu item deleted', severity: 'success' });
      fetchItems();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete menu item', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Menu Items</Typography>
          <Typography variant="body2" color="text.secondary">
            Every regular menu item across all businesses — edit pricing, category or availability, or remove one.
          </Typography>
        </Box>
      </Box>

      <Card sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by item or business name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search /></InputAdornment> } }}
        />
      </Card>

      <Card>
        {loading ? (
          <Box sx={{ py: 5 }}><LoadingAnimation /></Box>
        ) : error ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography color="error">Failed to load menu items.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Photo</strong></TableCell>
                  <TableCell><strong>Item</strong></TableCell>
                  <TableCell><strong>Business</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Price (<RialSymbol />)</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No menu items found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item) => {
                    const businessName = typeof item.business === 'object' ? item.business?.name : 'Unassigned';
                    return (
                      <TableRow key={item._id} hover>
                        <TableCell>
                          <Avatar
                            src={item.images?.[0]}
                            alt={item.name}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                          >
                            <FastfoodIcon fontSize="small" />
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.description?.substring(0, 50)}
                          </Typography>
                        </TableCell>
                        <TableCell>{businessName || 'Unassigned'}</TableCell>
                        <TableCell><Chip label={item.category} size="small" /></TableCell>
                        <TableCell>{item.price?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.status.replace('_', ' ')}
                            size="small"
                            color={item.status === 'active' ? 'success' : item.status === 'sold_out' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => openEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(item)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <PaginationControls
        page={page}
        total={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
      />

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit Menu Item
          <IconButton onClick={() => setEditOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>{editItem?.name}</Typography>
            <TextField
              label="Price (OMR)"
              type="number"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              slotProps={{ htmlInput: { step: 0.05, min: 0 } }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={editCategory}
                onChange={(e: SelectChangeEvent) => setEditCategory(e.target.value)}
              >
                {categories.map((c) => <MuiMenuItem key={c} value={c}>{c}</MuiMenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={editStatus}
                onChange={(e: SelectChangeEvent) => setEditStatus(e.target.value as MenuItemRow['status'])}
              >
                {statuses.map((s) => <MuiMenuItem key={s} value={s}>{s.replace('_', ' ')}</MuiMenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Menu Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
